import React from "react";
import { format } from "date-fns";
import StatusBadge, {
	type StatusBadgeVariant,
} from "@ui-components/Tag/StatusBadge";
import type { GenericTableColumn } from "@ui-components/Table/GenericTable";
import type {
	Subscription,
	SubscriptionServiceType,
	SubscriptionSortKey,
	SubscriptionState,
} from "./types";

/**
 * Presentation layer for the subscriptions table (CTX-26): labels, state
 * mapping, date helpers and the column definitions.
 *
 * Kept apart from `SubscriptionsPage` for the same reason `TagsData` is:
 * the column set is data, the page is orchestration, and tests can assert on
 * the columns without mounting the page.
 */

/** Service-type labels. Every value of the backend enum is covered so an unknown key is a type error. */
export const SERVICE_TYPE_LABEL: Record<SubscriptionServiceType, string> = {
	port: "Port",
	internet_enterprise: "Internet Enterprise",
	datacenter_interconnect: "DCI",
	cloud_connectivity: "Cloud Connectivity",
	bbaas: "BBaaS",
	bb_connector: "Backbone Connector",
	vrf: "VRF",
	fwint: "Firewall Interconnect",
	fwaas: "FWaaS",
	sdwan_hub: "SD-WAN Hub",
	sdwan_branch: "SD-WAN Branch",
	sdwan_int: "SD-WAN Interconnect",
	lag: "LAG",
};

/**
 * Lifecycle state → badge. Variants follow `StatusBadge`'s intent contract:
 * green for healthy, amber for "needs attention soon", red for lapsed, brand
 * for informational, neutral for terminal-but-benign.
 */
const STATE_BADGE: Record<
	SubscriptionState,
	{ label: string; variant: StatusBadgeVariant }
> = {
	active: { label: "Active", variant: "success" },
	reactivated: { label: "Reactivated", variant: "success" },
	future: { label: "Future", variant: "info" },
	expiring: { label: "Expiring", variant: "warning" },
	suspended: { label: "Suspended", variant: "warning" },
	expired: { label: "Expired", variant: "error" },
	renewed: { label: "Renewed", variant: "neutral" },
	cancelled: { label: "Cancelled", variant: "neutral" },
};

export const getStateBadge = (state: SubscriptionState) => STATE_BADGE[state];

/** States from which a subscription can no longer be renewed. */
const NON_RENEWABLE_STATES: ReadonlySet<SubscriptionState> = new Set<SubscriptionState>([
	"cancelled",
	// `renewed` already has a successor — CTX-23 refuses a second renewal so the
	// chain cannot fork. `future` has not started yet; renew its predecessor.
	"renewed",
	"future",
]);

export const canRenewSubscription = (row: Subscription): boolean =>
	!NON_RENEWABLE_STATES.has(row.state);

/** Window the CTX-19 expiry cron acts on; also drives the "ends soon" accent. */
export const EXPIRING_SOON_DAYS = 90;

const MS_PER_DAY = 86_400_000;

/** Matches the leading `YYYY-MM-DD` of an ISO date or datetime. */
const ISO_DATE_HEAD = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Parse a contract boundary off the wire, anchored to LOCAL midnight.
 *
 * Contract dates are calendar days, not instants: a term "ends on 28 Feb"
 * regardless of where it is read from. The API serializes them as UTC
 * midnight (`2025-02-28T00:00:00.000Z`), so handing that straight to
 * `new Date()` and formatting it renders the PREVIOUS day for anyone west of
 * UTC — a term ending 28 Feb showed as 27 Feb in UTC-6, which is wrong in a
 * view people use to decide renewals.
 *
 * Reading the calendar parts out of the string and rebuilding the date locally
 * keeps the day stable in every timezone, and keeps `daysUntil` comparing
 * local midnight against a local `now`.
 *
 * Returns null for absent or unparseable values.
 */
export function parseSubscriptionDate(value: string | null): Date | null {
	if (!value) return null;

	const isoHead = ISO_DATE_HEAD.exec(value);
	if (isoHead) {
		const [, year, month, day] = isoHead;
		return new Date(Number(year), Number(month) - 1, Number(day));
	}

	// Anything that is not an ISO date string (shouldn't happen, but the field
	// is typed as a plain string) falls back to normal parsing.
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** `dd MMM yyyy`, or an em dash when the term has no known bound. */
export function formatSubscriptionDate(value: string | null): string {
	const parsed = parseSubscriptionDate(value);
	return parsed ? format(parsed, "dd MMM yyyy") : "—";
}

export function daysUntil(value: string | null, now: Date): number | null {
	const parsed = parseSubscriptionDate(value);
	if (!parsed) return null;
	return Math.round((parsed.getTime() - now.getTime()) / MS_PER_DAY);
}

/** Accent for the "Fin" cell: red once lapsed, amber inside the expiry window. */
function endDateAccentClass(value: string | null, now: Date): string {
	const remaining = daysUntil(value, now);
	if (remaining === null) return "";
	if (remaining < 0) return "text-semantic-text-danger font-semibold";
	if (remaining <= EXPIRING_SOON_DAYS) return "text-semantic-text-warning font-semibold";
	return "";
}

export interface SortState {
	sortBy: SubscriptionSortKey;
	sortDir: "asc" | "desc";
}

/**
 * Clickable column header driving SERVER-side ordering.
 *
 * `GenericTable` sorts internally whenever it can derive a sort key from a
 * column (an `id`, an `accessor`, a string `header` or a `sortAccessor`).
 * That would only order the page in hand, which is wrong for a paginated
 * table. Passing a ReactNode header and no key-bearing field opts the column
 * out of that behavior, leaving ordering to the backend — see
 * `getColumnSortKey` in `GenericTable.tsx`.
 */
const SortableHeader: React.FC<{
	label: string;
	sortKey: SubscriptionSortKey;
	sort: SortState;
	onSort: (key: SubscriptionSortKey) => void;
}> = ({ label, sortKey, sort, onSort }) => {
	const isActive = sort.sortBy === sortKey;
	return (
		<button
			type="button"
			onClick={() => onSort(sortKey)}
			aria-label={`Sort by ${label}`}
			aria-sort={isActive ? (sort.sortDir === "asc" ? "ascending" : "descending") : "none"}
			className="flex items-center gap-1 text-left font-semibold text-semantic-text-secondary hover:text-semantic-text-primary"
		>
			<span>{label}</span>
			<span
				aria-hidden
				className={`text-[10px] leading-none ${
					isActive ? "text-semantic-text-brand" : "text-semantic-text-secondary opacity-50"
				}`}
			>
				{isActive && sort.sortDir === "desc" ? "▼" : "▲"}
			</span>
		</button>
	);
};

interface BuildColumnsOptions {
	/** Injected rather than read from the clock so rendering stays deterministic under test. */
	now: Date;
	sort: SortState;
	onSort: (key: SubscriptionSortKey) => void;
	onRenew: (row: Subscription) => void;
	onViewHistory: (row: Subscription) => void;
}

export function buildSubscriptionColumns({
	now,
	sort,
	onSort,
	onRenew,
	onViewHistory,
}: BuildColumnsOptions): GenericTableColumn<Subscription>[] {
	const sortable = (label: string, sortKey: SubscriptionSortKey) => (
		<SortableHeader label={label} sortKey={sortKey} sort={sort} onSort={onSort} />
	);

	return [
		{
			header: sortable("Subscription ID", "subscriptionId"),
			width: "170px",
			renderCell: (row) => (
				<div className="flex flex-col min-w-0">
					<span className="font-semibold text-semantic-text-brand truncate">
						{row.subscriptionId}
					</span>
					{row.renewalCount > 0 && (
						<span className="text-xs text-semantic-text-secondary">
							Renewal #{row.renewalCount}
						</span>
					)}
				</div>
			),
		},
		{
			header: sortable("Type", "serviceType"),
			width: "150px",
			wrapMode: "truncate",
			renderCell: (row) => SERVICE_TYPE_LABEL[row.serviceType],
		},
		{
			header: "Service",
			width: "200px",
			renderCell: (row) => (
				<div className="flex flex-col min-w-0">
					<span className="font-medium text-semantic-text-primary truncate">
						{row.serviceName}
					</span>
					{row.serviceReference && (
						<span className="text-xs text-semantic-text-secondary truncate">
							{row.serviceReference}
						</span>
					)}
				</div>
			),
		},
		{
			header: sortable("Customer", "customerName"),
			width: "170px",
			wrapMode: "truncate",
			renderCell: (row) => row.customerName,
		},
		{
			header: sortable("Status", "state"),
			width: "130px",
			renderCell: (row) => {
				const badge = getStateBadge(row.state);
				return <StatusBadge variant={badge.variant} label={badge.label} />;
			},
		},
		{
			header: sortable("Start", "contractStart"),
			width: "120px",
			wrapMode: "nowrap",
			renderCell: (row) => (
				<span className="tabular-nums">{formatSubscriptionDate(row.contractStart)}</span>
			),
		},
		{
			header: sortable("End", "contractEnd"),
			width: "150px",
			renderCell: (row) => {
				// The row's own term end and the service's effective validity are
				// different facts. They diverge after an early renewal: this
				// subscription still ends on `contractEnd`, but the service stays
				// covered until the end of the chain. Showing only the former would
				// read as "expiring" for a service that is already renewed.
				const extended =
					row.effectiveEndDate !== null && row.effectiveEndDate !== row.contractEnd;
				return (
					<div className="flex flex-col min-w-0">
						<span
							className={`tabular-nums ${endDateAccentClass(row.contractEnd, now)}`}
						>
							{formatSubscriptionDate(row.contractEnd)}
						</span>
						{extended && (
							<span className="text-xs text-semantic-text-secondary">
								Covered to {formatSubscriptionDate(row.effectiveEndDate)}
							</span>
						)}
					</div>
				);
			},
		},
		{
			header: "Renewals",
			width: "110px",
			renderCell: (row) =>
				row.renewalCount > 0 ? (
					<span className="inline-flex items-center justify-center min-w-[26px] h-[22px] px-2 rounded-round bg-semantic-bg-tertiary text-xs font-semibold tabular-nums">
						{row.renewalCount}
					</span>
				) : (
					<span className="text-semantic-text-secondary tabular-nums">0</span>
				),
		},
		{
			header: "Auto-renew",
			id: "autoRenew",
			width: "120px",
			renderCell: (row) =>
				row.autoRenew ? (
					<StatusBadge variant="success" label="Yes" withIcon={false} />
				) : (
					<span className="text-semantic-text-secondary">No</span>
				),
		},
		{
			header: "Actions",
			width: "180px",
			renderCell: (row) => {
				const renewable = canRenewSubscription(row);
				return (
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={() => onRenew(row)}
							disabled={!renewable}
							title={
								renewable
									? "Renew this subscription"
									: "This subscription can no longer be renewed"
							}
							className="h-8 px-2 rounded-sm border border-semantic-border-secondary text-sm text-semantic-text-primary hover:bg-semantic-state-active disabled:opacity-50 disabled:cursor-not-allowed"
						>
							Renew
						</button>
						<button
							type="button"
							onClick={() => onViewHistory(row)}
							title="Subscription history for this customer"
							className="h-8 px-2 rounded-sm border border-semantic-border-secondary text-sm text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-primary"
						>
							History
						</button>
					</div>
				);
			},
		},
	];
}
